<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { detectCategoryFromRoute } from 'dashboard/helper/routeCategory';
import Logo from 'next/icon/Logo.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';

const props = defineProps({
  activeCategory: {
    type: String,
    default: 'Conversation',
  },
});

const emit = defineEmits([
  'select',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { shouldShow } = usePolicy();
const unreadCount = useMapGetter('notifications/getUnreadCount');
const CATEGORY_ROUTE_CANDIDATES = {
  Inbox: ['inbox_view'],
  Conversation: ['home'],
  AiLab: ['ailab_ai_closer_index', 'ailab_assistants_index'],
  Contacts: ['contacts_dashboard_index'],
  Engage: ['engage_traffic_index'],
  TeamManagement: ['team_management_agents'],
  Reports: [
    'account_overview_reports',
    'account_last_7_days_reports',
    'total_chats_reports',
    'agent_reports_index',
    'inbox_reports_index',
    'team_reports_index',
    'label_reports_index',
    'vip_reports_index',
    'issue_reports_index',
    'token_usage_reports',
    'csat_reports',
    'sla_reports',
    'bot_reports',
    'telegram_bot_reports',
    'chat_history_export_reports',
    'case_closing_types_reports',
    'line_delivery_reports',
    'qa_overrides_reports_index',
    'cross_brand_reports_index',
  ],
  QADashboard: ['qa_dashboard_template_audit', 'qa_monitor_home'],
  Workforce: [
    'workforce_my_shift',
    'workforce_schedules',
    'workforce_assignment',
    'workforce_attendance',
    'workforce_live',
  ],
  Campaigns: [
    'campaigns_telegram_index',
    'campaigns_livechat_index',
    'campaigns_sms_index',
    'campaigns_whatsapp_index',
  ],
  Portals: [
    {
      name: 'portals_index',
      params: { navigationPath: 'portals_articles_index' },
    },
  ],
  Settings: [
    'general_settings_index',
    'custom_roles_list',
    'security_settings_index',
    'auditlogs_list',
    'billing_settings_index',
    'vip_settings_index',
    'agent_list',
    'settings_teams_list',
    'assignment_policy_index',
    'settings_issues_list',
    'sla_list',
    'settings_inbox_list',
    'settings_telegram_audience',
    'agent_bots',
    'automation_list',
    'automation_flows_list',
    'macros_wrapper',
    'canned_list',
    'flex_templates_list',
    'labels_list',
    'attributes_list',
    'line_stickers',
    'settings_applications',
  ],
};

const toScopedRoute = routeTarget => {
  if (!routeTarget) return null;
  if (typeof routeTarget === 'string') {
    return accountScopedRoute(routeTarget);
  }
  const { name, params = {}, query = {} } = routeTarget;
  if (!name) return null;
  return accountScopedRoute(name, params, query);
};

const canAccessRoute = routeTarget => {
  const scopedRoute = toScopedRoute(routeTarget);
  if (!scopedRoute) return false;

  let resolvedRoute;
  try {
    resolvedRoute = router.resolve(scopedRoute);
  } catch (error) {
    return false;
  }

  const permissions = resolvedRoute?.meta?.permissions || [];
  const featureFlag = resolvedRoute?.meta?.featureFlag || null;
  const installationTypes = resolvedRoute?.meta?.installationTypes || [];

  return shouldShow(featureFlag, permissions, installationTypes);
};

const firstAccessibleRoute = routeTargets =>
  routeTargets.find(routeTarget => canAccessRoute(routeTarget)) || null;

const canAccessSearch = computed(() => canAccessRoute('search'));

// Primary navigation categories with better icons
const categories = computed(() => {
  const baseCategories = [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-ph-tray-bold',
      hasChildren: false,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Inbox,
      badge: unreadCount.value > 0 ? unreadCount.value : null,
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-ph-chats-circle-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Conversation,
    },
    {
      name: 'AiLab',
      label: t('SIDEBAR.AILAB'),
      icon: 'i-ph-robot-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.AiLab,
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-ph-users-three-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Contacts,
    },
    {
      name: 'Engage',
      label: t('SIDEBAR.ENGAGE'),
      icon: 'i-ph-activity-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Engage,
    },
    {
      name: 'TeamManagement',
      label: t('SIDEBAR.TEAM_MANAGEMENT'),
      icon: 'i-ph-users-four-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.TeamManagement,
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-ph-chart-line-up-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Reports,
    },
    {
      name: 'QADashboard',
      label: t('SIDEBAR.QA_DASHBOARD'),
      icon: 'i-ph-shield-check-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.QADashboard,
    },
    {
      name: 'Workforce',
      label: t('WORKFORCE.SIDEBAR.LABEL'),
      icon: 'i-ph-calendar-check-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Workforce,
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-ph-megaphone-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Campaigns,
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-ph-books-bold',
      hasChildren: true,
      routeCandidates: CATEGORY_ROUTE_CANDIDATES.Portals,
    },
  ];

  return baseCategories
    .map(category => {
      const defaultRoute = firstAccessibleRoute(category.routeCandidates);
      if (!defaultRoute) return null;
      return { ...category, defaultRoute };
    })
    .filter(Boolean);
});

// Settings item for bottom section
const settingsItem = computed(() => {
  const defaultRoute = firstAccessibleRoute(CATEGORY_ROUTE_CANDIDATES.Settings);
  if (!defaultRoute) return null;

  return {
    name: 'Settings',
    label: t('SIDEBAR.SETTINGS'),
    icon: 'i-ph-gear-six-bold',
    hasChildren: true,
    defaultRoute,
  };
});

// Auto-detect category from current route
const detectedCategory = computed(() => {
  return detectCategoryFromRoute(route, props.activeCategory || '');
});

const isActive = categoryName => {
  return detectedCategory.value === categoryName;
};

const handleCategoryClick = category => {
  emit('select', category.name);
  // Navigate to the default route for this category
  if (category.defaultRoute) {
    const targetRoute = toScopedRoute(category.defaultRoute);
    if (targetRoute) {
      router.push(targetRoute);
    }
  }
};
</script>

<template>
  <aside
    class="primary-nav flex flex-col h-full z-50 transition-[width] duration-200 ease-in-out"
    style="background-color: var(--wlab-bg-sidebar)"
  >
    <!-- Logo -->
    <div class="flex items-center justify-center h-12 pt-3 flex-shrink-0">
      <Logo class="size-4" />
    </div>

    <!-- Navigation Items -->
    <nav
      class="flex-1 flex flex-col gap-0.5 px-1.5 py-1 overflow-y-auto no-scrollbar"
    >
      <!-- Main Categories -->
      <button
        v-for="category in categories"
        :key="category.name"
        type="button"
        class="primary-nav__item group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer relative"
        :class="{
          'bg-slate-200 dark:bg-white/10': isActive(category.name),
          'hover:bg-slate-100 dark:hover:bg-white/5': !isActive(category.name),
        }"
        @click="handleCategoryClick(category)"
      >
        <!-- Icon -->
        <span
          class="primary-nav__icon flex-shrink-0 size-4 text-slate-600 dark:text-white/70"
          :class="[
            category.icon,
            isActive(category.name)
              ? 'text-slate-900 dark:text-white'
              : 'group-hover:text-slate-900 dark:group-hover:text-white',
          ]"
        />

        <!-- Label (shows on hover) -->
        <span
          class="primary-nav__label whitespace-nowrap text-xs font-medium transition-opacity duration-150 opacity-0"
          :class="
            isActive(category.name)
              ? 'text-slate-900 dark:text-white'
              : 'text-slate-600 dark:text-white/70 group-hover:text-slate-900 dark:group-hover:text-white'
          "
        >
          {{ category.label }}
        </span>

        <!-- Badge -->
        <span
          v-if="category.badge"
          class="primary-nav__badge absolute top-0.5 ltr:left-[0.85rem] rtl:right-5 flex items-center justify-center min-w-[16px] h-[16px] px-0.5 text-[7px] font-bold rounded-full bg-n-ruby-9 text-white"
        >
          {{ category.badge > 99 ? '99+' : category.badge }}
        </span>
      </button>

      <!-- Spacer to push bottom items down -->
      <div class="flex-1" />

      <!-- Search -->
      <RouterLink
        v-if="canAccessSearch"
        :to="accountScopedRoute('search')"
        class="primary-nav__bottom-item group flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
      >
        <span
          class="i-ph-magnifying-glass-bold size-4 text-slate-600 dark:text-white/70 group-hover:text-slate-900 dark:group-hover:text-white"
        />
        <span
          class="primary-nav__label text-xs text-slate-600 dark:text-white/70 group-hover:text-slate-900 dark:group-hover:text-white whitespace-nowrap opacity-0"
        >
          {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
        </span>
      </RouterLink>

      <!-- Settings -->
      <button
        v-if="settingsItem"
        type="button"
        class="primary-nav__bottom-item group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer"
        :class="{
          'bg-slate-200 dark:bg-white/10': isActive('Settings'),
          'hover:bg-slate-100 dark:hover:bg-white/5': !isActive('Settings'),
        }"
        @click="handleCategoryClick(settingsItem)"
      >
        <span
          class="i-ph-gear-six-bold size-4 text-slate-600 dark:text-white/70"
          :class="
            isActive('Settings')
              ? 'text-slate-900 dark:text-white'
              : 'group-hover:text-slate-900 dark:group-hover:text-white'
          "
        />
        <span
          class="primary-nav__label text-xs font-medium whitespace-nowrap opacity-0"
          :class="
            isActive('Settings')
              ? 'text-slate-900 dark:text-white'
              : 'text-slate-600 dark:text-white/70 group-hover:text-slate-900 dark:group-hover:text-white'
          "
        >
          {{ settingsItem.label }}
        </span>
      </button>
    </nav>

    <!-- Profile Section -->
    <div class="flex-shrink-0 p-1.5 pb-4">
      <SidebarProfileMenu
        class="primary-nav__profile"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </aside>
</template>

<style scoped>
.primary-nav {
  width: 48px;
  min-width: 48px;
  position: relative;
  z-index: 100;
}

/* Expanded state - overlay on top without affecting layout */
.primary-nav:hover {
  width: 180px;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.3);
}

.primary-nav__label {
  display: none;
}

.primary-nav__item,
.primary-nav__bottom-item {
  justify-content: center;
}

.primary-nav:hover .primary-nav__item,
.primary-nav:hover .primary-nav__bottom-item {
  justify-content: flex-start;
}

.primary-nav:hover .primary-nav__label {
  display: block;
  opacity: 1;
}

.primary-nav__badge {
  transition:
    margin 0.15s ease,
    position 0s;
}

.primary-nav:hover .primary-nav__badge {
  position: static;
  margin-left: auto;
}

/* Bottom items label visibility */
.primary-nav__bottom-item .primary-nav__label {
  display: none;
}

.primary-nav:hover .primary-nav__bottom-item .primary-nav__label {
  display: block;
  opacity: 1;
}

/* Profile menu adjustments */
.primary-nav__profile :deep(.flex) {
  justify-content: center;
}

.primary-nav:hover .primary-nav__profile :deep(.flex) {
  justify-content: flex-start;
}

/* Hide text in profile when collapsed */
.primary-nav__profile
  :deep(
    span:not(.size-6):not(.i-lucide-chevron-up):not(.i-lucide-chevron-down)
  ) {
  display: none;
}

.primary-nav:hover
  .primary-nav__profile
  :deep(
    span:not(.size-6):not(.i-lucide-chevron-up):not(.i-lucide-chevron-down)
  ) {
  display: inline;
}

/* Light mode - dark text */
body:not(.dark) .primary-nav :deep(*) {
  color: #475569 !important;
}

/* Light mode - Orange hover ONLY */
body:not(.dark) .primary-nav :deep(button:hover),
body:not(.dark) .primary-nav :deep(a:hover) {
  background: rgba(0, 102, 255, 0.12) !important;
}

body:not(.dark) .primary-nav :deep(button:hover *),
body:not(.dark) .primary-nav :deep(a:hover *) {
  color: #004db3 !important;
}

/* Light mode - Orange active (only .bg-slate-200 class) */
body:not(.dark) .primary-nav :deep(button.bg-slate-200) {
  background: rgba(0, 102, 255, 0.18) !important;
}

body:not(.dark) .primary-nav :deep(button.bg-slate-200 *) {
  color: #004db3 !important;
}

/* Dark mode - light text */
body.dark .primary-nav :deep(*) {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Dark mode - Orange hover ONLY */
body.dark .primary-nav :deep(button:hover),
body.dark .primary-nav :deep(a:hover) {
  background: rgba(51, 136, 255, 0.15) !important;
}

body.dark .primary-nav :deep(button:hover *),
body.dark .primary-nav :deep(a:hover *) {
  color: #66a3ff !important;
}

/* Montserrat for the left mini sidebar */
:global(.primary-nav),
:global(.primary-nav *) {
  font-family:
    'Montserrat',
    'Inter',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif !important;
}
</style>
